import { Helmet } from "react-helmet-async";
import Layout from "@/components/layout/Layout";
import HeroSection from "@/components/sections/HeroSection";
import ProblemSection from "@/components/sections/ProblemSection";
import HomepageIntroSection from "@/components/sections/HomepageIntroSection";
import ValuePropsTabbedSection from "@/components/sections/ValuePropsTabbedSection";
import ServicesSection from "@/components/sections/ServicesSection";
import ProcessSectionHorizontal from "@/components/sections/ProcessSectionHorizontal";
import PackagesSection from "@/components/sections/PackagesSection";
import TestimonialsSection from "@/components/sections/TestimonialsSection";
import FaqSection from "@/components/sections/FaqSection";
import AreasSection from "@/components/sections/AreasSection";
import CtaSection from "@/components/sections/CtaSection";
import ProjectsSection from "@/components/sections/ProjectsSection";
import FeaturedBlogSection from "@/components/sections/FeaturedBlogSection";
import TrustSection from "@/components/sections/TrustSection";
import MiniCtaSection from "@/components/sections/MiniCtaSection";
import MobileStickyCTA from "@/components/layout/MobileStickyCTA";
import LocalBusinessSchema from "@/components/seo/LocalBusinessSchema";
import LazySection from "@/components/ui/LazySection";
import { useSettings } from "@/contexts/SettingsContext";
import { getOptimizedImageUrl } from "@/lib/imageUtils";
const DEFAULT_SECTIONS_ORDER = [
  'hero', 'problem', 'intro', 'trust', 'value_props', 'services', 'mini_cta', 'process', 
  'testimonials', 'projects', 'pricing', 'areas', 'faqs', 'blog', 'cta'
];

const defaultValueProps = [
  { title: "Like-new results", description: "Deep cleaning plus expert after-care keeps surfaces brighter for longer – not just a quick rinse." },
  { title: "Right method, right surface", description: "Pressure washing, soft washing or gentle scrubbing – we match the method to protect your property." },
  { title: "Restore, not just clean", description: "Re-sanding, sealing and colour revival included where needed. Your surfaces will look and last better." },
  { title: "No mess left behind", description: "We collect sludge, rinse edges and top up joints. You'll never know we were there (except for the results)." },
  { title: "Clear, friendly service", description: "Honest quotes, progress photos, and we always show up when we say we will." },
  { title: "Trusted locally", description: "Built on My Tidy Garden's reputation for quality work across Bournemouth and Poole since 2018." },
];

const defaultProblems = [
  { text: "Your driveway is covered in moss, weeds, and stubborn stains" },
  { text: "The patio furniture looks tired and faded" },
  { text: "Paths are slippery and dangerous in wet weather" },
  { text: "You've tried DIY cleaning but results never last" },
  { text: "Professional quotes seem expensive with no clear breakdown" },
];

const Index = () => {
  const { settings, isLoading } = useSettings();

  // If settings are still loading, render nothing (avoid showing default marketing copy)
  // The Layout/Header/Footer will still render, providing immediate visual feedback
  if (isLoading && !settings) {
    return (
      <>
        <Helmet>
          <title>MyClean Exteriors | Driveway & Patio Cleaning Bournemouth & Poole</title>
          <meta name="description" content="Professional pressure washing, soft washing and surface restoration for homes and managed properties across Bournemouth and Poole. Free quotes available." />
          <link rel="canonical" href="https://mycleanexteriors.co.uk/" />
        </Helmet>
        <Layout>
          {/* Minimal placeholder while settings load - prevents flash of default copy */}
          <div className="min-h-[60vh]" />
        </Layout>
      </>
    );
  }

  // Use settings images - all images should now be in Supabase
  const heroImage = settings?.hero_image;
  
  // Problem section images - passed raw to ProblemSection which handles its own optimization
  const problemImage = settings?.problem_image;
  const solutionImage = settings?.solution_image;
  
  // Value props background - pass raw URL, ValuePropsSection handles optimization
  const valuePropsBackgroundImage = settings?.value_props_background;

  // Section backgrounds from CMS
  const sectionBackgrounds = (settings as any)?.section_backgrounds || {};

  // Section text colors from CMS
  const sectionTextColors = (settings as any)?.section_text_colors || {};

  // Hero content - use CMS values (defaults only as true fallback when CMS field is empty)
  const heroTitle = settings?.homepage_hero_title || "Transform Your Home's First Impression";
  const heroSubtitle = settings?.homepage_hero_subtitle || "Professional driveway, patio and exterior cleaning that brings your outdoor spaces back to life. Serving Bournemouth, Poole and surrounding areas.";
  const secondaryCtaText = settings?.homepage_secondary_cta_text || "View Our Services";
  const secondaryCtaLink = settings?.homepage_secondary_cta_link || "/services";
  const heroTagline = (settings as any)?.hero_tagline || "";
  
  // Hero phone button - use phone from global settings
  const heroPhoneText = settings?.phone || "";
  const heroPhoneLink = settings?.phone ? `tel:${settings.phone.replace(/\s/g, "")}` : "";

  // Problem section content
  const problemTitle = settings?.homepage_problem_title || "Sound Familiar?";
  const problemSubtitle = settings?.homepage_problem_subtitle || "Your outdoor spaces deserve better";
  const problems = settings?.homepage_problems || defaultProblems;

  // Value props content
  const valuePropsTitle = settings?.homepage_value_props_title || "Why Homeowners Choose Us";
  const valuePropsSubtitle = settings?.homepage_value_props_subtitle || "We're not just cleaners – we're craftspeople who take pride in transforming your outdoor spaces";
  const valueProps = settings?.homepage_value_props || defaultValueProps;

  // Services section content
  const servicesTitle = settings?.homepage_services_title || "Expert Exterior Cleaning Services";
  const servicesSubtitle = settings?.homepage_services_subtitle || "From block paving to wooden decking, we have the right tools and techniques for every surface";

  // Testimonials section content
  const testimonialsTitle = settings?.homepage_testimonials_title || "What Our Customers Say";
  const testimonialsSubtitle = settings?.homepage_testimonials_subtitle || "Don't just take our word for it – here's what our customers have to say";

  // FAQ section content
  const faqTitle = settings?.homepage_faq_title || "Frequently Asked Questions";
  const faqSubtitle = settings?.homepage_faq_subtitle || "Got questions? We've got answers";
  const faqLimit = settings?.homepage_faq_limit || 5;

  // CTA section content
  const ctaTitle = settings?.homepage_cta_title || "Ready to Transform Your Outdoor Space?";
  const ctaSubtitle = settings?.homepage_cta_subtitle || "Get a free, no-obligation quote today. We'll assess your property and recommend the best approach.";
  const ctaImage = settings?.homepage_cta_image || undefined;

  // Areas section content
  const areasMapEmbedUrl = settings?.areas_map_embed_url || undefined;
  const areasSectionTitle = settings?.areas_section_title || "Areas We Cover";
  const areasSectionSubtitle = settings?.areas_section_subtitle || "Proudly serving Bournemouth, Poole and beyond";
  const areasMapVisible = settings?.areas_map_visible ?? true;
  const areasMapMode = settings?.areas_map_mode || "place";
  const areasMapQuery = settings?.areas_map_query || undefined;
  const areasMapCenter = settings?.areas_map_center || undefined;
  const areasMapZoom = settings?.areas_map_zoom || undefined;
  const areasMapType = settings?.areas_map_type || undefined;
  // Street View camera controls
  const areasMapHeading = (settings as any)?.areas_map_heading ?? undefined;
  const areasMapPitch = (settings as any)?.areas_map_pitch ?? undefined;
  const areasMapFov = (settings as any)?.areas_map_fov ?? undefined;

  // Pricing/Packages section content
  const pricingTitle = settings?.pricing_packages_title || "Pricing & Packages";
  const pricingSubtitle = settings?.pricing_packages_subtitle || "Transparent pricing. No hidden surprises.";
  const pricingPackages = settings?.pricing_packages || undefined;
  const pricingFooterNote = settings?.pricing_footer_note || undefined;

  // Problem section spacing
  const problemImagePaddingMobile = (settings as any)?.problem_section_image_padding_mobile || "pt-32";
  const problemImagePaddingDesktop = (settings as any)?.problem_section_image_padding_desktop || "lg:pt-40";
  const problemCtaPaddingMobile = (settings as any)?.problem_section_cta_padding_mobile || "pt-12";
  const problemCtaPaddingDesktop = (settings as any)?.problem_section_cta_padding_desktop || "md:pt-16";

  // Homepage Introduction section
  const introTitle = settings?.homepage_intro_title || "";
  const introContentAbove = (settings as any)?.homepage_intro_content_above || "";
  const introContentBeside = settings?.homepage_intro_content || "";
  const introImage = settings?.homepage_intro_image || "";
  const introImageAlt = settings?.homepage_intro_image_alt || "";
  const introImagePosition = ((settings as any)?.homepage_intro_image_position || "right") as 'left' | 'right';

  // Trust Section content from CMS
  const trustOwnerName = (settings as any)?.trust_owner_name || undefined;
  const trustOwnerTitle = (settings as any)?.trust_owner_title || undefined;
  const trustOwnerBio = (settings as any)?.trust_owner_bio || undefined;
  const trustOwnerImage = (settings as any)?.trust_owner_image || undefined;
  const trustIndicators = (settings as any)?.trust_indicators || undefined;
  const trustCtaText = (settings as any)?.trust_cta_text || undefined;
  const trustCtaLink = (settings as any)?.trust_cta_link || undefined;
  const trustBadgeValue = (settings as any)?.trust_badge_value || undefined;
  const trustBadgeLabel = (settings as any)?.trust_badge_label || undefined;

  // Mini CTA Section content from CMS
  const miniCtaTitle = (settings as any)?.mini_cta_title || "Have questions? Let's talk!";
  const miniCtaButtonText = (settings as any)?.mini_cta_button_text || "Get a Free Quote";
  const miniCtaButtonLink = (settings as any)?.mini_cta_button_link || "/quote";
  const miniCtaVariant = ((settings as any)?.mini_cta_variant || "subtle") as 'default' | 'accent' | 'subtle';
  const miniCtaShowPhone = (settings as any)?.mini_cta_show_phone ?? true;

  // Process Section content from CMS
  const processTitle = (settings as any)?.homepage_process_title || "How It Works";
  const processSubtitle = (settings as any)?.homepage_process_subtitle || "Our proven step-by-step cleaning method";
  const processSteps = (settings as any)?.homepage_process_steps || undefined;
  
  // Section visibility and order
  const sectionsVisible = (settings as any)?.homepage_sections_visible || {};
  const sectionsOrder = settings?.homepage_sections_order || DEFAULT_SECTIONS_ORDER;

  // Helper to check visibility
  const isVisible = (key: string) => sectionsVisible[key] !== false;

  // Section components map
  const sectionComponents: Record<string, React.ReactNode> = {
    hero: (
      <HeroSection
        key="hero"
        title={heroTitle}
        subtitle={heroSubtitle}
        backgroundImage={heroImage}
        overlayOpacity={0.5}
        secondaryCtaText={secondaryCtaText}
        secondaryCtaLink={secondaryCtaLink}
        phoneText={heroPhoneText}
        phoneLink={heroPhoneLink}
        tagline={heroTagline}
        backgroundColor={sectionBackgrounds?.hero || undefined}
        textColor={sectionTextColors?.hero || undefined}
      />
    ),
    problem: (
      <ProblemSection 
        key="problem"
        title={problemTitle}
        subtitle={problemSubtitle}
        problems={problems}
        problemImage={problemImage} 
        solutionImage={solutionImage}
        imagePaddingMobile={problemImagePaddingMobile}
        imagePaddingDesktop={problemImagePaddingDesktop}
        ctaPaddingMobile={problemCtaPaddingMobile}
        ctaPaddingDesktop={problemCtaPaddingDesktop}
        backgroundColor={sectionBackgrounds?.problem || undefined}
        textColor={sectionTextColors?.problem || undefined}
        reducedTopPadding
      />
    ),
    intro: (introContentAbove || introContentBeside) ? (
      <HomepageIntroSection
        key="intro"
        title={introTitle}
        contentAbove={introContentAbove}
        contentBeside={introContentBeside}
        image={introImage}
        imageAlt={introImageAlt}
        imagePosition={introImagePosition}
        ctaText={(settings as any)?.homepage_intro_cta_text}
        ctaLink={(settings as any)?.homepage_intro_cta_link}
        backgroundColor={sectionBackgrounds?.intro || undefined}
        textColor={sectionTextColors?.intro || undefined}
      />
    ) : null,
    trust: (
      <TrustSection 
        key="trust"
        ownerName={trustOwnerName}
        ownerTitle={trustOwnerTitle}
        ownerBio={trustOwnerBio}
        ownerImage={trustOwnerImage}
        indicators={trustIndicators}
        ctaText={trustCtaText}
        ctaLink={trustCtaLink}
        backgroundColor={sectionBackgrounds?.trust || undefined}
        textColor={sectionTextColors?.trust || undefined}
        badgeValue={trustBadgeValue}
        badgeLabel={trustBadgeLabel}
      />
    ),
    value_props: (
      <ValuePropsTabbedSection 
        key="value_props"
        valueProps={valueProps}
        title={valuePropsTitle}
        subtitle={valuePropsSubtitle}
        backgroundImage={valuePropsBackgroundImage}
        variant="tabs"
        textColor={sectionTextColors?.value_props || undefined}
      />
    ),
    services: (
      <ServicesSection
        key="services"
        title={servicesTitle}
        subtitle={servicesSubtitle}
        variant="carousel"
        backgroundColor={sectionBackgrounds?.services || undefined}
        textColor={sectionTextColors?.services || undefined}
      />
    ),
    mini_cta: (
      <MiniCtaSection
        key="mini_cta"
        title={miniCtaTitle}
        ctaText={miniCtaButtonText}
        ctaLink={miniCtaButtonLink}
        variant={miniCtaVariant}
        showPhone={miniCtaShowPhone}
      />
    ),
    process: (
      <ProcessSectionHorizontal 
        key="process"
        title={processTitle}
        subtitle={processSubtitle}
        steps={processSteps}
        backgroundColor={sectionBackgrounds?.process || undefined}
        textColor={sectionTextColors?.process || undefined}
      />
    ),
    testimonials: (
      <LazySection key="testimonials-lazy" minHeight="400px" rootMargin="200px">
        <TestimonialsSection 
          key="testimonials"
          title={testimonialsTitle}
          subtitle={testimonialsSubtitle}
          backgroundColor={sectionBackgrounds?.testimonials || undefined}
          textColor={sectionTextColors?.testimonials || undefined}
        />
      </LazySection>
    ),
    projects: (
      <LazySection key="projects-lazy" minHeight="500px" rootMargin="200px">
        <ProjectsSection key="projects" />
      </LazySection>
    ),
    pricing: (
      <LazySection key="pricing-lazy" minHeight="400px" rootMargin="200px">
        <PackagesSection 
          key="pricing"
          title={pricingTitle}
          subtitle={pricingSubtitle}
          packages={pricingPackages}
          footerNote={pricingFooterNote}
          backgroundColor={sectionBackgrounds?.pricing || undefined}
          textColor={sectionTextColors?.pricing || undefined}
        />
      </LazySection>
    ),
    areas: (
      <LazySection key="areas-lazy" minHeight="400px" rootMargin="200px">
        <AreasSection 
          key="areas"
          primaryOnly
          showMap={areasMapVisible}
          mapEmbedUrl={areasMapEmbedUrl}
          title={areasSectionTitle}
          subtitle={areasSectionSubtitle}
          mapMode={areasMapMode}
          mapQuery={areasMapQuery}
          mapCenter={areasMapCenter}
          mapZoom={areasMapZoom}
          mapType={areasMapType}
          mapHeading={areasMapHeading}
          mapPitch={areasMapPitch}
          mapFov={areasMapFov}
          backgroundColor={sectionBackgrounds?.areas || undefined}
          textColor={sectionTextColors?.areas || undefined}
        />
      </LazySection>
    ),
    faqs: (
      <LazySection key="faqs-lazy" minHeight="400px" rootMargin="200px">
        <FaqSection 
          key="faqs"
          title={faqTitle}
          subtitle={faqSubtitle}
          limit={faqLimit}
          backgroundColor={sectionBackgrounds?.faqs || undefined}
          textColor={sectionTextColors?.faqs || undefined}
        />
      </LazySection>
    ),
    blog: (
      <LazySection key="blog-lazy" minHeight="400px" rootMargin="200px">
        <FeaturedBlogSection key="blog" />
      </LazySection>
    ),
    cta: (
      <CtaSection 
        key="cta"
        variant="accent"
        title={ctaTitle}
        subtitle={ctaSubtitle}
        image={ctaImage}
        backgroundColor={sectionBackgrounds?.cta || undefined}
        textColor={sectionTextColors?.cta || undefined}
      />
    ),
  };

  // Render sections in order
  const renderSections = () => {
    return sectionsOrder
      .filter(key => isVisible(key) && sectionComponents[key])
      .map(key => sectionComponents[key]);
  };

  return (
    <>
      <Helmet>
        <title>MyClean Exteriors | Driveway & Patio Cleaning Bournemouth & Poole</title>
        <meta name="description" content="Professional pressure washing, soft washing and surface restoration for homes and managed properties across Bournemouth and Poole. Free quotes available." />
        <link rel="canonical" href="https://mycleanexteriors.co.uk/" />
      </Helmet>
      <LocalBusinessSchema pageUrl="https://mycleanexteriors.co.uk" />

      <Layout>
        {renderSections()}
        <MobileStickyCTA />
      </Layout>
    </>
  );
};

export default Index;
